/**
 * Pure HTTP client for backend API calls.
 *
 * No authentication logic. Defaults to `credentials: 'include'` so the
 * browser sends VoiceDojo's `vd_session` cookie on every request.
 */

import { handleHttpError } from './errors.js'

/**
 * Replace `{name}` / `{name:type}` ASP.NET route templates in `endpoint`
 * with values from `data`. Consumed keys are removed from the returned
 * `remaining` object so they are not also sent as body / query-string fields.
 */
function interpolateRoute(
  endpoint: string,
  data: Record<string, unknown>,
): { url: string; remaining: Record<string, unknown> } {
  const remaining = { ...data };
  const url = endpoint.replace(/\{(\w+)(?::\w+)?\}/g, (match, name: string) => {
    if (Object.prototype.hasOwnProperty.call(remaining, name)) {
      const value = remaining[name];
      delete remaining[name];
      return encodeURIComponent(String(value));
    }
    return match;
  });
  return { url, remaining };
}

export interface RequestOptions {
  baseUrl?: string
  timeout?: number
  headers?: Record<string, string>
  credentials?: RequestCredentials
}

let defaultOptions: RequestOptions = {
  baseUrl: '',
  timeout: 30000,
  headers: {},
  credentials: 'include', // Default to include for cookie support (vd_session)
}

/**
 * Configure default request options.
 */
export function configureApi(options: Partial<RequestOptions>): void {
  defaultOptions = { ...defaultOptions, ...options }
}

/**
 * Execute a typed HTTP request to a backend endpoint.
 * This is a pure HTTP client with no authentication logic.
 */
export async function executeQuery<TQuery, TResult>(
  endpoint: string,
  method: string = 'GET',
  query?: TQuery,
  options?: Partial<RequestOptions>
): Promise<TResult> {
  const config = { ...defaultOptions, ...options }

  // Interpolate {name} / {name:type} route templates from the query object.
  // Path params are stripped from the remaining object so they are not also
  // sent as body fields or appended as query-string parameters.
  let effectiveEndpoint = endpoint;
  let effectiveQuery: TQuery | undefined = query;
  if (query != null && typeof query === 'object') {
    const { url: interpolated, remaining } = interpolateRoute(
      endpoint,
      query as Record<string, unknown>,
    );
    effectiveEndpoint = interpolated;
    effectiveQuery = remaining as TQuery;
  }

  const url = `${config.baseUrl ?? ''}${effectiveEndpoint}`

  // Prepare headers
  const headers: Record<string, string> = {
    ...config.headers,
  }

  // Set content-type for requests with body
  if (Boolean(effectiveQuery) && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
    headers['Content-Type'] = 'application/json'
  }

  // Prepare request
  const requestInit: RequestInit = {
    method,
    headers,
    credentials: config.credentials,
    signal: typeof config.timeout === 'number' ? AbortSignal.timeout(config.timeout) : undefined,
  }

  // Add body for non-GET requests with query data
  if (Boolean(effectiveQuery) && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
    requestInit.body = JSON.stringify(effectiveQuery)
  }

  // Handle GET requests with query parameters
  let finalUrl = url
  if (Boolean(effectiveQuery) && method === 'GET') {
    const searchParams = new URLSearchParams()
    Object.entries(effectiveQuery as Record<string, unknown>).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })
    const queryString = searchParams.toString()
    if (queryString) {
      finalUrl += `?${queryString}`
    }
  }

  try {
    const response = await fetch(finalUrl, requestInit)

    if (!response.ok) {
      throw await handleHttpError(response)
    }

    // Handle empty responses
    const contentType = response.headers.get('content-type')
    if (!contentType?.includes('application/json')) {
      return undefined as TResult
    }

    return (await response.json()) as TResult
  } catch (error) {
    // Re-throw API errors as-is
    if (error instanceof Error && error.name.endsWith('Error')) {
      throw error
    }

    // Wrap other errors
    throw new Error(`Request failed: ${String(error)}`)
  }
}
